import { Link, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useLiveCounts } from "../lib/useLiveCounts";
import { ChatsCircle, Flag, House, Hash, Bookmark, Code, Bug, Cpu, Detective, Briefcase, Newspaper, Trophy, EnvelopeOpen, Lightning, Sparkle, X, Flask, Heart, Users, ShoppingCart, ShieldCheck, Buildings, BookmarkSimple, MagnifyingGlass, CaretDown, CaretRight, Plus, GraduationCap, Compass, HardDrives, SealCheck, Terminal, CalendarBlank, SignOut } from "@phosphor-icons/react";
import TopBadgeCollectors from "./TopBadgeCollectors";
import { useAuth } from "../context/AuthContext";
import { useMobileNav } from "../context/MobileNavContext";
import { useScrollColumn } from "../lib/useScrollColumn";
import { FrictionFeedback } from "./FrictionFeedback";

const subforumIcons = {
    netsec: Cpu,
    webhacking: Bug,
    "reverse-eng": Code,
    ctf: Flag,
    blueteam: Detective,
    career: Hash,
};

// Sidebar widget collapse state — persisted across reloads so each user keeps
// their preferred shape of the sidebar. Three independent flags.
const SECTION_KEY = "hc_sidebar_sections";
function readSections() {
    try { return JSON.parse(localStorage.getItem(SECTION_KEY) || "{}"); }
    catch { return {}; }
}
function writeSections(s) {
    try { localStorage.setItem(SECTION_KEY, JSON.stringify(s)); } catch { /* private mode */ }
}

export default function LeftSidebar({ onOpenAI }) {
    const [subforums, setSubforums] = useState([]);
    const [tags, setTags] = useState([]);
    const location = useLocation();
    const { user, logout } = useAuth();
    const { open, close } = useMobileNav();
    // HC-UI-006: dialog focus management — focus close on open, Escape closes,
    // focus returns to the trigger after close. Tab is trapped by the global
    // FocusTrap via the role="dialog" aria-modal semantics on the drawer.
    const closeBtnRef = useRef(null);
    const prevFocusRef = useRef(null);
    useEffect(() => {
        if (!open) return;
        prevFocusRef.current = document.activeElement;
        const t = setTimeout(() => closeBtnRef.current?.focus(), 80);
        const onEsc = (e) => { if (e.key === "Escape") close(); };
        window.addEventListener("keydown", onEsc);
        return () => {
            clearTimeout(t);
            window.removeEventListener("keydown", onEsc);
            prevFocusRef.current?.focus?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const { notifications: notifCount, chat: chatCount, modPending } = useLiveCounts();
    const { t } = useTranslation();
    const scrollRef = useScrollColumn("left-sidebar");
    const [sections, setSections] = useState(() => {
        // HC-UI-005: on small screens collapse discovery widgets by default so
        // the drawer stays short and core destinations come first.
        const small = typeof window !== "undefined" && window.innerWidth < 1024;
        return {
            more: false, communities: !small, tags: !small, badges: !small,
            ...readSections(),
        };
    });
    const toggleSection = useCallback((key) => {
        setSections((prev) => {
            const next = { ...prev, [key]: !prev[key] };
            writeSections(next);
            return next;
        });
    }, []);

    useEffect(() => {
        api.get("/subforums").then(({ data }) => setSubforums(data)).catch(() => {});
        api.get("/tags?limit=12").then(({ data }) => setTags(data)).catch(() => {});
    }, []);

    const navItem = (to, icon, label, testid, badge, pinned = false) => {
        const active = location.pathname === to;
        const Icon = icon;
        const showBadge = typeof badge === "number" && badge > 0;
        const idleText = pinned ? "text-slate-100" : "text-slate-400";
        const hoverText = pinned ? "hover:text-white" : "hover:text-slate-200";
        // iter167i — fire-and-forget tap counter. Never blocks navigation,
        // never surfaces errors. Backend caps abuse at 10k/IP/day.
        const onTap = () => {
            close();
            try {
                api.post("/_/nav-tap", { item: testid }).catch(() => {});
            } catch { /* ignore */ }
        };
        return (
            <Link
                to={to}
                data-testid={testid}
                onClick={onTap}
                aria-current={active ? "page" : undefined}
                className={`group flex items-center gap-3 px-3 py-2.5 lg:py-2 text-sm rounded-lg transition-colors active:scale-[0.98] ${
                    active ? "bg-cyan-500/10 text-cyan-300" : `${idleText} hover:bg-white/5 ${hoverText}`
                }`}
            >
                <Icon size={16} weight={pinned ? "fill" : "duotone"} className={active ? "text-cyan-300" : (pinned ? "text-cyan-400/70" : "text-slate-500 group-hover:text-slate-300")} />
                <span className="flex-1 flex items-center gap-1.5">
                    {label}
                    {pinned && !active && (
                        <span aria-hidden className="inline-block w-1 h-1 rounded-full bg-cyan-400/70" />
                    )}
                </span>
                {showBadge && (
                    <span
                        data-testid={`${testid}-badge`}
                        className={`min-w-[18px] h-[18px] px-1.5 inline-flex items-center justify-center text-[10px] font-mono font-bold rounded-md leading-none ${
                            active ? "bg-cyan-400 text-slate-950" : "bg-cyan-500/15 text-cyan-300"
                        }`}
                    >
                        {badge > 99 ? "99+" : badge}
                    </span>
                )}
            </Link>
        );
    };

    const content = (
        <>
            {user && (
                <StreakNudge onNavigate={close} />
            )}
            {/* SEC-OPS AI assistant launcher — visible on mobile only (lg:hidden) since
              * the desktop TopNav already has its own pill. Renders for any logged-in
              * user so they can open the helper without hunting for it. */}
            {user && onOpenAI && (
                <button
                    onClick={() => { close(); onOpenAI(); }}
                    data-testid="sidebar-ai"
                    className="lg:hidden w-full flex items-center gap-2.5 px-3 py-2.5 mb-2 text-sm rounded-sm border border-purple-500/40 bg-purple-500/5 hover:bg-purple-500/10 text-purple-300 hover:text-purple-200 transition-colors"
                >
                    <Lightning size={16} weight="fill" />
                    <span className="flex-1 text-left">SEC-OPS AI</span>
                    <span className="text-[9px] uppercase tracking-widest opacity-70">helper</span>
                </button>
            )}
            {/* Highlighted mod / admin console entry — only visible to mods + admins.
              * Rendered above the main nav so it's the first thing they see when the
              * mobile drawer opens (since the desktop top-nav admin pill is hidden
              * on small screens). */}
            {user && (user.role === "admin" || user.role === "mod") && (
                <Link
                    to="/admin"
                    onClick={close}
                    data-testid="sidebar-admin"
                    className={`flex items-center gap-2.5 px-3 py-2.5 mb-3 text-sm rounded-sm transition-colors border ${
                        location.pathname === "/admin"
                            ? "bg-cyan-500/15 text-cyan-300 border-cyan-500/40"
                            : "bg-white/[0.03] text-cyan-400 border-white/10 hover:bg-cyan-500/10 hover:border-cyan-500/40"
                    }`}
                >
                    <ShieldCheck size={16} weight="duotone" />
                    <span className="flex-1">Mod Console</span>
                    {modPending > 0 ? (
                        <span
                            data-testid="sidebar-admin-badge"
                            className="min-w-[18px] h-[18px] px-1.5 inline-flex items-center justify-center text-[10px] font-mono font-bold rounded-sm leading-none bg-amber-500/20 text-amber-300 border border-amber-500/40"
                            title={`${modPending} open report${modPending === 1 ? "" : "s"}`}
                        >
                            {modPending > 99 ? "99+" : modPending}
                        </span>
                    ) : (
                        <span className="text-[9px] uppercase tracking-widest opacity-70">
                            {user.role === "admin" ? "admin" : "mod"}
                        </span>
                    )}
                </Link>
            )}
            <div className="space-y-1">
                {user?.account_type === "recruiter" ? (
                    <>
                        {navItem("/recruiter", Briefcase, "Recruiter Dashboard", "sidebar-recruiter")}
                        {navItem("/recruiter/talent", MagnifyingGlass, "Browse Talent", "sidebar-recruiter-browse")}
                        {navItem("/recruiter/search", MagnifyingGlass, "Advanced Search", "sidebar-recruiter-search")}
                        {navItem("/recruiter/saved", BookmarkSimple, "Saved Candidates", "sidebar-recruiter-saved")}
                        {navItem("/jobs", Briefcase, "Jobs Board", "sidebar-jobs")}
                        {navItem("/jobs/new", Sparkle, "Post a Job", "sidebar-recruiter-post-job")}
                        {navItem("/companies/new", Buildings, "New Company Page", "sidebar-recruiter-company")}
                        {/* iter145: Recruiter chrome — removed `news`, `chat`,
                            and the // communities section below. Inbox stays
                            because candidate replies + outreach notifications
                            land there. */}
                        {navItem("/inbox", EnvelopeOpen, t("nav.inbox"), "sidebar-inbox", notifCount)}
                        {navItem("/billing", Sparkle, "Billing", "sidebar-billing")}
                    </>
                ) : (
                    <>
                        {/* iter — restructured hacker nav into 5 always-open groups
                            (learn → practice → compete → work → community) + a
                            collapsed MORE. Mirrors the HTB/THM journey model so
                            newcomers scan by intent instead of reading a flat list. */}
                        {navItem("/", House, t("nav.home"), "sidebar-home")}

                        <NavGroup id="learn" label={t("nav.group_learn")} accent="cyan">
                            {navItem("/learn", GraduationCap, "Academy", "sidebar-learn")}
                            {navItem("/tracks", Compass, "Career Tracks", "sidebar-tracks")}
                            {navItem("/certifications", SealCheck, "Certifications", "sidebar-certifications")}
                        </NavGroup>

                        <NavGroup id="practice" label={t("nav.group_practice")} accent="emerald">
                            {navItem("/labs", Flask, t("nav.labs"), "sidebar-labs", undefined, true)}
                            {navItem("/box", Terminal, "Attack Box", "sidebar-box", undefined, true)}
                            {navItem("/network-labs", HardDrives, "Network Labs", "sidebar-network-labs", undefined, true)}
                            {navItem("/investigations", MagnifyingGlass, "Investigations", "sidebar-investigations")}
                        </NavGroup>

                        <NavGroup id="compete" label={t("nav.group_compete")} accent="amber">
                            {navItem("/ctf", Flag, t("nav.ctf"), "sidebar-ctf")}
                            {navItem("/tournaments", Trophy, "Tournaments", "sidebar-tournaments")}
                            {navItem("/events", CalendarBlank, "Events", "sidebar-events")}
                            {navItem("/multiplayer", Users, "Multiplayer", "sidebar-multiplayer")}
                            {navItem("/leaderboard", Trophy, t("nav.leaderboard"), "sidebar-leaderboard")}
                            {user && navItem("/quests", Lightning, "Quests & XP", "sidebar-quests")}
                        </NavGroup>

                        <NavGroup id="work" label={t("nav.group_work")} accent="violet">
                            {navItem("/jobs", Briefcase, t("nav.jobs"), "sidebar-jobs", undefined, true)}
                            {navItem("/bounties", Bug, t("nav.bounties"), "sidebar-bounties", undefined, true)}
                            {/* iter-current: "My Bug Reports" removed — internal company
                                bounty programs sunset; directory links out to public platforms. */}
                        </NavGroup>

                        <NavGroup id="community" label={t("nav.group_community")} accent="cyan">
                            {navItem("/communities", ChatsCircle, t("nav.communities"), "sidebar-communities")}
                            {navItem("/news", Newspaper, t("nav.news"), "sidebar-news")}
                            {navItem("/q", Hash, "Q&A", "sidebar-qa")}
                            {user && navItem("/chat", ChatsCircle, t("nav.chat"), "sidebar-chat", chatCount)}
                            {user && navItem("/inbox", EnvelopeOpen, t("nav.inbox"), "sidebar-inbox", notifCount)}
                        </NavGroup>

                        {user && navItem("/billing", Sparkle, "Billing & Plans", "sidebar-billing")}

                        {/* MORE — collapsed utility drawer */}
                        <button
                            onClick={() => toggleSection("more")}
                            data-testid="sidebar-more-toggle"
                            className="w-full flex items-center gap-2.5 px-3 pt-3 pb-1 text-[9px] uppercase tracking-[0.18em] font-mono text-slate-500 hover:text-cyan-300 rounded-sm transition-colors"
                        >
                            {sections.more ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
                            <span className="flex-1 text-left">// {t("nav.more")}</span>
                            {!sections.more && <span className="text-[9px] text-slate-600 normal-case tracking-normal">+5</span>}
                        </button>
                        {sections.more && (
                            <div className="space-y-1">
                                {navItem("/tools", Cpu, t("nav.tools"), "sidebar-tools")}
                                {navItem("/bookmarks", Bookmark, "Bookmarks", "sidebar-bookmarks")}
                                {user && navItem("/refer", Heart, "Refer & Earn", "sidebar-refer")}
                                {user && navItem("/developer", Code, "Developer API", "sidebar-developer")}
                                {navItem("/roadmap", Sparkle, "Roadmap", "sidebar-roadmap")}
                            </div>
                        )}
                    </>
                )}
            </div>

            <div className="mt-6">
                {/* iter145: hacker forums — recruiters don't need this list. */}
                {user?.account_type !== "recruiter" && (
                    <>
                        <button
                            onClick={() => toggleSection("communities")}
                            data-testid="sidebar-section-communities"
                            aria-expanded={!!sections.communities}
                            aria-controls="sidebar-communities-list"
                            className="w-full flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-slate-500 hover:text-cyan-300 transition-colors"
                        >
                            {sections.communities ? <CaretDown size={9} weight="bold" /> : <CaretRight size={9} weight="bold" />}
                            <span className="flex-1 text-left">// communities</span>
                        </button>
                        {sections.communities && (
                            <div id="sidebar-communities-list"><CommunitiesList subforums={subforums} onClose={close} /></div>
                        )}
                    </>
                )}
            </div>

            {/* iter140: trending tags + week-leaders are hacker-community signals
                and add no value for recruiters who landed here to find talent. */}
            {user?.account_type !== "recruiter" && (
                <>
                    <div className="mt-6">
                        <button
                            onClick={() => toggleSection("tags")}
                            data-testid="sidebar-section-tags"
                            aria-expanded={!!sections.tags}
                            aria-controls="sidebar-tags-list"
                            className="w-full flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-slate-500 hover:text-cyan-300 transition-colors"
                        >
                            {sections.tags ? <CaretDown size={9} weight="bold" /> : <CaretRight size={9} weight="bold" />}
                            <span className="flex-1 text-left">// trending tags</span>
                        </button>
                        {sections.tags && (
                            <div id="sidebar-tags-list"><TagsList tags={tags} onClose={close} /></div>
                        )}
                    </div>

                    <div className="mt-6">
                        <button
                            onClick={() => toggleSection("badges")}
                            data-testid="sidebar-section-badges"
                            aria-expanded={!!sections.badges}
                            aria-controls="sidebar-badges-list"
                            className="w-full flex items-center gap-1.5 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-slate-500 hover:text-cyan-300 transition-colors"
                        >
                            {sections.badges ? <CaretDown size={9} weight="bold" /> : <CaretRight size={9} weight="bold" />}
                            <span className="flex-1 text-left">// week leaders</span>
                        </button>
                        {sections.badges && (
                            <div id="sidebar-badges-list"><TopBadgeCollectors days={7} /></div>
                        )}
                    </div>
                </>
            )}

            <div className="mt-8 px-3 text-[9px] tracking-widest text-slate-500 leading-relaxed">
                <div className="divider-glow mb-3"></div>
                ETHICAL DISCLOSURE ONLY.<br/>
                NO LIVE EXPLOITS.<br/>
                BE EXCELLENT TO EACH OTHER.
                <div className="mt-3"><FrictionFeedback /></div>
            </div>
        </>
    );

    return (
        <>
            {/* Desktop sidebar — always visible at lg+ */}
            <aside
                ref={scrollRef}
                data-testid="sidebar-desktop"
                className="col-fade hidden lg:block w-60 shrink-0 h-full overflow-y-auto scrollbar-hidden border-r border-white/10 py-6 px-2"
            >
                {content}
            </aside>

            {/* Mobile/tablet drawer — slides in from the left when open */}
            <div
                data-testid="sidebar-mobile-backdrop"
                onClick={close}
                className={`lg:hidden fixed inset-0 bg-black/60 backdrop-blur-[2px] z-50 transition-opacity duration-300 ease-out ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                aria-hidden={!open}
            />
            <aside
                id="sidebar-mobile"
                data-testid="sidebar-mobile"
                aria-hidden={!open}
                role={open ? "dialog" : undefined}
                aria-modal={open ? "true" : undefined}
                aria-label="Site navigation"
                className={`lg:hidden fixed top-0 left-0 bottom-0 w-72 max-w-[85vw] bg-[#0b0f19] border-r border-white/10 z-50 overflow-y-auto scrollbar-hidden pb-4 px-2 transform transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform shadow-2xl shadow-black/50 ${open ? "translate-x-0" : "-translate-x-full"}`}
            >
                {/* HC-UI-005: sticky heading row — close stays reachable at any scroll position */}
                <div className="sticky top-0 z-10 bg-[#0b0f19] border-b border-white/5 flex items-center justify-between px-2 py-2 mb-3 -mx-2">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-mono pl-2">// navigation</div>
                    <button
                        ref={closeBtnRef}
                        onClick={close}
                        data-testid="sidebar-mobile-close"
                        className="flex items-center justify-center w-11 h-11 text-slate-400 hover:text-slate-200 rounded-lg hover:bg-white/5 transition-colors"
                        aria-label="Close menu"
                    >
                        <X size={20} />
                    </button>
                </div>
                {open && content}
                {open && (
                    <button
                        onClick={() => { close(); logout(); }}
                        data-testid="drawer-logout-btn"
                        className="mt-4 mx-2 flex items-center gap-2 min-h-[44px] px-3 w-[calc(100%-16px)] rounded-lg border border-rose-500/25 text-rose-300 hover:bg-rose-500/10 font-mono text-[11px] uppercase tracking-widest transition-colors"
                    >
                        <SignOut size={16} weight="duotone" /> log out
                    </button>
                )}
            </aside>
        </>
    );
}


/** Always-open nav section with a tiny mono header. Groups the primary nav
 *  by intent (learn/practice/compete/work/community). */
function NavGroup({ id, label, accent = "cyan", children }) {
    return (
        <div className="pt-3" data-testid={`sidebar-group-${id || label}`}>
            <div className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">{label}</div>
            <div className="space-y-0.5">{children}</div>
        </div>
    );
}


/** Shows 5 subforums first; "load more" expands by 5 each click. */
function CommunitiesList({ subforums, onClose }) {
    const [shown, setShown] = useState(5);
    const visible = subforums.slice(0, shown);
    const hasMore = shown < subforums.length;
    return (
        <>
            <div className="space-y-0.5 mt-1">
                {visible.map((s) => {
                    const Icon = subforumIcons[s.slug] || Hash;
                    return (
                        <Link
                            key={s.slug}
                            to={`/h/${s.slug}`}
                            onClick={onClose}
                            data-testid={`sidebar-sub-${s.slug}`}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-400 hover:text-cyan-400 hover:bg-slate-800/30 rounded-sm"
                        >
                            <Icon size={14} weight="duotone" style={{ color: s.color }} />
                            <span className="font-mono">h/{s.name}</span>
                        </Link>
                    );
                })}
            </div>
            {hasMore && (
                <button
                    onClick={() => setShown((s) => s + 5)}
                    data-testid="sidebar-communities-more"
                    className="w-full flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-mono text-slate-500 hover:text-cyan-300 rounded-sm hover:bg-slate-800/30 transition-colors"
                >
                    <Plus size={9} weight="bold" />
                    <span>load {Math.min(5, subforums.length - shown)} more</span>
                </button>
            )}
        </>
    );
}


/** Shows 8 tags first; "load more" expands by 8 each click. */
function TagsList({ tags, onClose }) {
    const [shown, setShown] = useState(8);
    const visible = tags.slice(0, shown);
    const hasMore = shown < tags.length;
    return (
        <>
            <div className="flex flex-wrap gap-1 px-2 mt-1">
                {visible.map((t) => (
                    <Link
                        key={t.name}
                        to={`/?tag=${t.name}`}
                        onClick={onClose}
                        className="text-[10px] uppercase tracking-wider px-2 py-1 bg-slate-800/50 hover:bg-cyan-500/10 hover:text-cyan-400 text-slate-300 border border-white/10 hover:border-cyan-500/30 rounded-sm transition-colors"
                        data-testid={`tag-${t.name}`}
                    >
                        #{t.name}
                    </Link>
                ))}
            </div>
            {hasMore && (
                <button
                    onClick={() => setShown((s) => s + 8)}
                    data-testid="sidebar-tags-more"
                    className="w-full mt-1.5 flex items-center justify-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-mono text-slate-500 hover:text-cyan-300 rounded-sm hover:bg-slate-800/30 transition-colors"
                >
                    <Plus size={9} weight="bold" />
                    <span>+{Math.min(8, tags.length - shown)} more</span>
                </button>
            )}
        </>
    );
}

function StreakNudge({ onNavigate }) {
    const [streak, setStreak] = useState(null);
    useEffect(() => {
        api.get("/me/academy/streak").then(({ data }) => setStreak(data)).catch(() => {});
    }, []);
    if (!streak) return null;
    const n = streak.streak || 0;
    const done = streak.items_today;
    return (
        <Link to="/quests" onClick={onNavigate} data-testid="sidebar-streak-nudge"
              className={`lg:hidden w-full flex items-center gap-2.5 px-3 py-2.5 mb-2 text-sm rounded-sm border transition-colors
                  ${done ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-300" : "border-amber-500/40 bg-amber-500/5 text-amber-300 hover:bg-amber-500/10"}`}>
            <Lightning size={16} weight="fill" className="shrink-0" />
            <span className="flex-1 min-w-0 text-left font-mono text-[12px] whitespace-nowrap truncate flex items-center gap-1.5"
                  title={n > 0 ? `${n}-day streak${done ? " · secured today" : " — don't break it"}` : "start a streak today"}>
                <span className="truncate">{n > 0 ? `${n}-day streak${done ? " · secured" : " · keep it"}` : "start a streak"}</span>
                {streak.freeze_tokens > 0 && <span className="text-sky-300 shrink-0">❄{streak.freeze_tokens}</span>}
            </span>
            <span className="shrink-0 text-[9px] uppercase tracking-widest opacity-70 whitespace-nowrap">{done ? "quests" : "claim →"}</span>
        </Link>
    );
}
